import React, { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useDeviceInfo } from '@chargify/device-provider';
import {
  BodyText,
  CreditCard,
  BankAccount,
  Button,
  Box,
} from '@chargify/custom-ui-components';

import { useComponentSettings } from '../../hooks';
import { Container } from './BilingDetailsContainer.styles';

import {
  SubscriptionBilling,
  SubscriptionBillingAddress,
} from '../../models/subscription/types';

export type BillingDetailsContainerProps = {
  /**
   * Product catalog identifier
   */
  catalogId: number;
  /**
   * Subscription identifier
   */
  subscriptionId: number;
  /**
   * Subscription billing details
   */
  billingDetails: SubscriptionBilling;
  /**
   * Indicator that payment profile is expierd
   */
  isExpired?: boolean;
};

const BillingDetailsContainer = ({
  catalogId,
  subscriptionId,
  billingDetails,
  isExpired,
}: BillingDetailsContainerProps) => {
  const { device } = useDeviceInfo();
  const navigate = useNavigate();

  const {
    paymentProfile: {
      visible: paymentProfileEnabled,
      addressFields,
      editAllowed,
    },
  } = useComponentSettings();

  const { t } = useTranslation(['subscriptions-manager']);
  const { creditCard, bankAccount, address, paymentProfileConnected } =
    billingDetails;

  const filteredAddressFields =
    address &&
    addressFields.reduce<Partial<SubscriptionBillingAddress>>(
      (obj, key) => ({ ...obj, [key]: address[key] }),
      {}
    );

  const creditCardDetails = {
    ...creditCard,
    type: creditCard?.cardType,
    number: creditCard?.maskedNumber,
  };

  const accountDetails = {
    ...bankAccount,
    accountNumber: bankAccount?.maskedAccountNumber,
    routingNumber: bankAccount?.maskedRoutingNumber,
  };
  const cardSize = device === 'mobile' ? 'small' : 'regular';
  const status = isExpired ? 'error' : 'normal';

  const onUpdatePaymentProfile = useCallback(() => {
    navigate(`/paymentProfiles/${catalogId}/${subscriptionId}`);
  }, []);

  if (!paymentProfileEnabled) {
    return (
      <div>
        <BodyText>
          {t('billing_details.payment_method_is_hidden', {
            ns: 'subscriptions-manager',
          })}
        </BodyText>
      </div>
    );
  }

  return (
    <Container>
      {!paymentProfileConnected && (
        <>
          <BodyText>
            {t('billing_details.there_is_no_payment_method', {
              ns: 'subscriptions-manager',
            })}
          </BodyText>
          {editAllowed && (
            <Box marginTop={3}>
              <Button
                label={t('billing_details.add_payment_method', {
                  ns: 'subscriptions-manager',
                })}
                onClick={onUpdatePaymentProfile}
                color="primary"
                size="medium"
                iconLeft="plus"
              />
            </Box>
          )}
        </>
      )}
      {paymentProfileConnected && (
        <>
          {creditCard && (
            <CreditCard
              size={cardSize}
              card={creditCardDetails}
              addressDetails={filteredAddressFields}
              status={status}
            />
          )}
          {bankAccount && (
            <BankAccount
              size={cardSize}
              accountDetails={accountDetails}
              addressDetails={filteredAddressFields}
            />
          )}
          {editAllowed && (
            <Box marginTop={3}>
              <Button
                label={t('billing_details.edit_payment_method', {
                  ns: 'subscriptions-manager',
                })}
                onClick={onUpdatePaymentProfile}
                color="secondary"
                size="medium"
                iconLeft="edit"
              />
            </Box>
          )}
        </>
      )}
    </Container>
  );
};
export default BillingDetailsContainer;
